import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Home, Building2, HardHat, ArrowLeft, MailCheck, CheckCircle2 } from 'lucide-react';

import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import Seo from '@/components/Seo';
import Turnstile, { TurnstileStatus } from '@/components/Turnstile';
import ResendConfirmation from '@/components/ResendConfirmation';
import { guardAuth, guardMessage, reportAuthFailure, captchaPendingMessage } from '@/lib/authGuard';
import PurchaseSteps from '@/components/PurchaseSteps';
import { authLink, localizeNext, readPurchaseIntent } from '@/lib/purchaseIntent';
import { parseRole } from '@/lib/roles';

import { MarketplaceDisclaimer, AcceptTermsCheckbox } from '@/components/MarketplaceDisclaimer';
import SmsConsentCheckbox from '@/components/SmsConsentCheckbox';
import { ACCEPT_REQUIRED, TERMS_VERSION, bi } from '@/data/legal';
import { TRADES, US_STATES, UserType, tradeLabel, homeownerCategories, gcTrades } from '@/data/constants';

/*
 * text-base on phones: iOS Safari ZOOMS the whole page into any input whose
 * font is smaller than 16px, which pushed the form sideways mid-signup and
 * left buyers pinching to get back. 16px on mobile, 14px from sm: up.
 */
const inputCls =
  'w-full rounded-lg border border-slate-300 px-3 py-3 text-base text-slate-900 placeholder-slate-400 focus:border-slate-900 focus:outline-none sm:py-2.5 sm:text-sm';
const labelCls = 'mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500';

const Register: React.FC = () => {
  const { t, path, lang } = useI18n();
  const { signUp, profile, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // parseRole() accepts the short aliases links use (e.g. role=sub from the
  // post-payment page) and ignores junk, so the form always matches a real type.
  const initialRole = parseRole(params.get('role'));
  /**
   * COMMERCIAL INTENT. The homepage "Commercial" card links here with
   * ?role=homeowner&type=commercial. There is NO fourth account type — a
   * commercial property owner is a project-owner account that posts projects
   * with project_type='commercial'. This flag only changes the heading and adds
   * an explanatory note, so nothing about auth, RLS or the profile row changes.
   */
  const commercial = params.get('type') === 'commercial';

  /**
   * `next` lets a flow resume after account creation — the post-payment return
   * page uses it so a buyer WITHOUT an account activates it here and is sent
   * straight back to /payment-complete, which then applies the paid credits and
   * continues to Projects. `email` pre-fills the address that was paid with, so
   * the verified webhook can be matched to this new account.
   */
  const nextUrl = params.get('next') || '';
  /** 'lead' | 'plan' when the visitor arrived by clicking Buy / Join. */
  const buyParam = params.get('buy');
  const buy: 'lead' | 'plan' | null = buyParam === 'lead' || buyParam === 'plan' ? buyParam : null;
  /**
   * ALREADY PAID.  The post-payment page sends a buyer without an account here
   * with next=/payment-complete. They have paid — this form must be as short
   * as the Buy flow's, and must say plainly that nothing will be charged again.
   */
  const afterPayment = /\/(payment-complete|pago-completado)(\?|$)/.test(nextUrl);
  /** Short form: during Buy / Join AND right after paying. */
  const lean = !!buy || afterPayment;
  /**
   * ONE SCREEN LESS DURING A PURCHASE.  (2026-09-30 conversion review)
   * Buyers used to see a "What kind of business are you?" screen before the
   * form. Only two answers can buy (subcontractor / general contractor) and
   * both buy leads and memberships the same way, so a buyer now lands on the
   * form directly with Subcontractor selected and a one-tap switch at the top.
   * A homeowner role is never pre-selected for a buyer.
   */
  const startRole: UserType | null =
    initialRole && !(lean && initialRole === 'homeowner') ? initialRole : lean ? 'subcontractor' : initialRole;
  const [role, setRole] = useState<UserType | null>(startRole);
  const [f, setF] = useState<Record<string, string>>(() => {
    const prefill = params.get('email') || '';
    // GigGone is a California marketplace: pre-select CA (still changeable)
    // so almost nobody has to open the 50-state picker on a phone.
    const base: Record<string, string> = (US_STATES as readonly string[]).includes('CA') ? { state: 'CA' } : {};
    return prefill ? { ...base, email: prefill } : base;
  });

  /** The trade of the lead being bought, if it is a valid chip for this role. */
  const leadTradeFor = (r: UserType | null): string | null => {
    if (!r || r === 'homeowner') return null;
    const it = readPurchaseIntent();
    if (!it || it.kind !== 'lead' || !it.trade) return null;
    const opts: { slug: string }[] = r === 'gc' ? gcTrades() : TRADES;
    return opts.some((o) => o.slug === it.trade) ? it.trade : null;
  };
  const [trades, setTrades] = useState<string[]>(() => {
    const pre = leadTradeFor(startRole);
    return pre ? [pre] : [];
  });

  /** True from the moment this page's own sign-up starts (see redirect below). */
  const submitting = useRef(false);
  const [accepted, setAccepted] = useState(false);
  /** A2P 10DLC: optional SMS consent — never pre-checked, never required. */
  const [smsConsent, setSmsConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // Turnstile token — re-verified server-side by auth-guard before signUp runs.
  const [captcha, setCaptcha] = useState('');
  // Widget state + reset trigger. Tokens are single-use, so every server check
  // must be followed by a reset or the next attempt re-sends a dead token —
  // the "stuck at verification" bug reported on 2026-09-24.
  const [captchaStatus, setCaptchaStatus] = useState<TurnstileStatus>('loading');
  const [captchaReset, setCaptchaReset] = useState(0);
  /**
   * True once Supabase has created the auth user but is waiting for the email
   * address to be confirmed. Drives the confirmation screen below.
   */
  const [pending, setPending] = useState(false);
  /** Password visibility — reduces typos on phone keyboards. */
  const [showPw, setShowPw] = useState(false);
  /** The email is already registered — show a one-tap "Sign in with this email". */
  const [existing, setExisting] = useState(false);



  const set = (k: string, v: string) => setF((p) => ({ ...p, [k]: v }));
  const toggleTrade = (slug: string) =>
    setTrades((p) => (p.includes(slug) ? p.filter((x) => x !== slug) : [...p, slug]));

  /** Choosing an account type also pre-ticks the trade of the lead being bought. */
  const pickRole = (r: UserType) => {
    setRole(r);
    const pre = leadTradeFor(r);
    if (pre) setTrades((p) => (p.length ? p : [pre]));
  };

  /**
   * ALREADY SIGNED IN? SKIP THIS PAGE.  (2026-09-30 review)
   * A buyer who is signed in (other tab, Back button after signing up, an old
   * "Join" link) used to land on a blank sign-up form with no way forward
   * except filling it in again. Send them straight on to what they were doing.
   * Skipped while this page's own submit is running — it navigates itself.
   */
  useEffect(() => {
    if (authLoading || !profile || pending || submitting.current) return;
    navigate(
      localizeNext(nextUrl || (buy ? readPurchaseIntent()?.returnTo : null), path, path('/dashboard')),
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, profile?.id, pending]);

  const roles = [
    { key: 'homeowner' as UserType, icon: Home, title: t('home.homeownerTitle'), desc: t('home.homeownerDesc') },
    { key: 'gc' as UserType, icon: Building2, title: t('home.gcTitle'), desc: t('home.gcDesc') },
    { key: 'subcontractor' as UserType, icon: HardHat, title: t('home.subTitle'), desc: t('home.subDesc') },
  ];

  /** Show the error AND bring it into view (on phones it sits below the fold). */
  const fail = (msg: string) => {
    setError(msg);
    window.setTimeout(() => {
      document.getElementById('gg-register-error')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 30);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setExisting(false);
    /*
      NAME THE MISSING FIELDS. "Please complete all required fields" made
      people scroll a long form hunting for the blank box — a classic place
      to give up. The message now lists exactly what is still empty.
    */
    const es = lang === 'es';
    const need: [boolean, string][] =
      role === 'homeowner'
        ? [
            [!f.first_name, t('auth.firstName')],
            [!f.last_name, t('auth.lastName')],
            [!f.phone, t('common.phone')],
            [!f.city, t('common.city')],
            [!f.state, t('common.state')],
          ]
        : [
            // Company is optional for subcontractors (their name is used).
            [role === 'gc' && !f.company_name, t('auth.companyName')],
            [!f.contact_name, role === 'gc' ? t('auth.contactName') : t('auth.ownerName')],
            [!f.phone, t('common.phone')],
            [!f.city, t('common.city')],
            [!f.state, t('common.state')],
            // Years in business and employees are OPTIONAL (2026-09-30):
            // two fewer reasons to abandon sign-up; editable in Profile Settings.
          ];
    const missing = need.filter(([m]) => m).map(([, label]) => label);
    if (missing.length) return fail(`${es ? 'Falta completar' : 'Please fill in'}: ${missing.join(', ')}.`);
    if (!/^\S+@\S+\.\S+$/.test(f.email || '')) return fail(t('auth.errEmail'));
    if ((f.password || '').length < 6) return fail(t('auth.errPassword'));
    // Trades are optional during a purchase / right after paying (folded away, editable later).
    if (role !== 'homeowner' && !lean && trades.length === 0) return fail(t('auth.errTrades'));
    if (!accepted) return fail(bi(ACCEPT_REQUIRED, lang));

    // Do not spend a server check (and a signup rate-limit hit) on a token the
    // widget has not produced yet.
    const pendingMsg = captchaPendingMessage(captchaStatus, captcha, lang);
    if (pendingMsg) return fail(pendingMsg);

    setBusy(true);

    /*
     * NEVER STUCK ON "Creating account…". A dropped connection on a phone
     * used to throw out of this handler, leaving the button disabled forever
     * with no message. Any unexpected failure now re-enables the button and
     * says what to do.
     */
    let res: Awaited<ReturnType<typeof signUp>>;
    try {
      // Automated-signup defence: server-verified Turnstile + 5 registrations
      // per IP per hour, with every rejection logged to mkt_security_events.
      const verdict = await guardAuth('register', f.email, captcha);
      // The token is now spent (pass or fail) — fetch a fresh one for any retry.
      setCaptchaReset((n) => n + 1);
      if (!verdict.ok) {
        setBusy(false);
        return fail(guardMessage(verdict, t, lang));
      }

      submitting.current = true;
      res = await signUp(f.email, f.password, {
        user_type: role || 'subcontractor',
        first_name: f.first_name,
        last_name: f.last_name,
        // Subcontractors may leave company blank: their own name stands in, so
        // every downstream label (CRM, lead history, admin) still has a name.
        company_name: (f.company_name || '').trim() || (role === 'subcontractor' ? (f.contact_name || '').trim() : f.company_name),
        contact_name: f.contact_name,
        phone: f.phone,
        city: f.city,
        state: f.state,
        years_in_business: f.years_in_business ? Number(f.years_in_business) : null,
        employees: f.employees ? Number(f.employees) : null,
        // Service areas are optional now: empty means "my city".
        service_areas: ((f.service_areas || '').trim() || f.city || '').split(',').map((s) => s.trim()).filter(Boolean),
        trades,
        preferred_language: lang,
        terms_accepted_at: new Date().toISOString(),
        terms_version: TERMS_VERSION,
        // Exactly what the user ticked — false when the box was left empty.
        sms_opt_in: smsConsent === true,
      });
    } catch {
      submitting.current = false;
      setBusy(false);
      setCaptchaReset((n) => n + 1);
      return fail(
        es
          ? 'Se perdió la conexión. Revise su señal y toque el botón otra vez — no se cobró nada.'
          : 'The connection dropped. Check your signal and tap the button again — nothing was charged.',
      );
    }
    setBusy(false);
    if (res.error) {
      submitting.current = false;
      void reportAuthFailure('register', f.email).catch(() => undefined);
      /*
       * EMAIL ALREADY REGISTERED. The raw provider text ("User already
       * registered") gave no way forward — returning customers who clicked
       * Buy and filled in the sign-up form by habit were stuck. Say it plainly
       * and offer a one-tap sign-in with the email filled in.
       */
      if (/already\s+(been\s+)?registered|already\s+exists|user_already_exists/i.test(res.error)) {
        setExisting(true);
        return fail(
          es
            ? 'Ya existe una cuenta con este correo. Inicie sesión para continuar.'
            : 'This email already has an account. Sign in to continue.',
        );
      }
      return fail(res.error);
    }

    /**
     * EMAIL CONFIRMATION REQUIRED.  (P0 fix — "I never got a confirmation")
     *
     * Previously ANY non-error result navigated straight to /dashboard. When
     * Supabase requires address confirmation there is no session yet, so the
     * profile row could not be written, the dashboard bounced the user back to
     * /login, and nothing on screen ever said "check your email". People
     * reasonably concluded the account was created when it was not.
     *
     * Now the registration turns into an explicit confirmation step.
     */
    if (res.needsConfirmation) {
      setPending(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    // Resume the interrupted flow: the exact lead (checkout auto-opens) or the
    // exact plan checkout — in the language the buyer is using NOW.
    // localizeNext() refuses off-site targets.
    navigate(localizeNext(nextUrl || readPurchaseIntent()?.returnTo, path, path('/dashboard')));

  };

  /**
   * CONFIRMATION STEP. Shown only when Supabase created the auth user but is
   * waiting for the address to be confirmed. It states plainly what happened,
   * where the email went, and what to do if it does not arrive — instead of
   * the old silent redirect that made a pending signup look like a finished
   * account.
   */
  if (pending) {
    return (
      <div className="mx-auto max-w-xl px-4 py-14 sm:px-6">
        <Seo title={lang === 'es' ? 'Confirme su correo' : 'Confirm your email'} description="" noindex />
        <div className="rounded-2xl border-2 border-emerald-300 bg-emerald-50 p-6 sm:p-8">
          <p className="flex items-center gap-2 text-xl font-extrabold text-emerald-900">
            <MailCheck className="h-6 w-6" />
            {lang === 'es' ? 'Revise su correo' : 'Check your email'}
          </p>
          <p className="mt-3 text-sm leading-relaxed text-emerald-900">
            {lang === 'es'
              ? 'Enviamos un enlace de confirmación a:'
              : 'We sent a confirmation link to:'}
          </p>
          <p className="mt-1 break-all rounded-lg bg-white px-3 py-2 text-sm font-bold text-slate-900">
            {f.email}
          </p>
          <p className="mt-4 text-sm leading-relaxed text-emerald-900">
            {afterPayment
              ? lang === 'es'
                ? 'Abra ese enlace y sus créditos pagados se aplican a esta cuenta automáticamente. No se le cobrará de nuevo.'
                : 'Open that link and your paid credits are applied to this account automatically. You will not be charged again.'
              : buy
              ? lang === 'es'
                ? 'Abra ese enlace y lo llevamos directo al pago de lo que estaba comprando. No tiene que buscarlo de nuevo.'
                : 'Open that link and we take you straight back to paying for what you picked. You will not have to find it again.'
              : lang === 'es'
              ? 'Su cuenta NO estará activa hasta que abra ese enlace. Después de confirmarla podrá iniciar sesión y usar su panel.'
              : 'Your account is NOT active until you open that link. Once confirmed you can sign in and use your dashboard.'}
          </p>
          <ul className="mt-4 space-y-1.5 text-sm text-emerald-800">
            <li>
              {lang === 'es'
                ? '• Si no llega en unos minutos, revise la carpeta de spam o correo no deseado.'
                : '• If it does not arrive within a few minutes, check your spam or junk folder.'}
            </li>
            <li>
              {lang === 'es'
                ? '• El enlace abre GIGGONE en este mismo navegador.'
                : '• The link opens GIGGONE in this same browser.'}
            </li>
          </ul>

          {/*
            RESEND — same component, same 60-second cooldown and same
            server-side rate limit as the sign-in page, so the two recovery
            paths can never drift apart.
          */}
          <ResendConfirmation email={f.email || ''} lang={lang} className="mt-5" />

          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to={authLink(path('/login'), nextUrl, buy || undefined, f.email ? `email=${encodeURIComponent(f.email)}` : '')}
              className="inline-flex min-h-[44px] items-center justify-center rounded-xl bg-emerald-600 px-5 py-3 text-sm font-bold text-white hover:bg-emerald-700"
            >
              {lean
                ? lang === 'es'
                  ? 'Ya confirmé — continuar'
                  : 'I confirmed — continue'
                : lang === 'es'
                ? 'Ir a iniciar sesión'
                : 'Go to sign in'}
            </Link>
            {/* No "browse" detour in the middle of a purchase — it is the
                exact wrong turn buyers took and never came back from. */}
            {!lean && (
              <Link
                to={path('/jobs')}
                className="inline-flex min-h-[44px] items-center justify-center rounded-xl border-2 border-emerald-300 px-5 py-3 text-sm font-bold text-emerald-800 hover:bg-emerald-100"
              >
                {lang === 'es' ? 'Ver proyectos' : 'Browse projects'}
              </Link>
            )}
          </div>
          <p className="mt-5 text-xs text-emerald-800">
            {lang === 'es' ? '¿Correo equivocado? ' : 'Wrong address? '}
            <button
              type="button"
              onClick={() => setPending(false)}
              className="font-bold underline"
            >
              {lang === 'es' ? 'Volver y corregirlo' : 'Go back and fix it'}
            </button>
          </p>
        </div>
      </div>
    );
  }

  if (!role) {
    // Buying a lead or a membership is contractor-only. Showing the
    // homeowner card here only created a wrong turn (a homeowner account can
    // never finish the purchase), so a buyer sees just the two that can.
    const shown = buy ? roles.filter((r) => r.key !== 'homeowner') : roles;
    return (
      <div className="mx-auto max-w-4xl px-4 py-14 sm:px-6">
        <Seo title={t('auth.chooseRole')} description={t('auth.chooseRoleSub')} noindex />
        {buy && (
          <div className="mx-auto max-w-2xl">
            <PurchaseSteps buy={buy} lang={lang} />
          </div>
        )}
        <h1 className="text-center text-3xl font-extrabold tracking-tight text-slate-900">
          {buy ? (lang === 'es' ? '¿Qué tipo de empresa tiene?' : 'What kind of business are you?') : t('auth.chooseRole')}
        </h1>
        <p className="mt-2 text-center text-slate-500">
          {buy
            ? lang === 'es'
              ? 'Toque uno. Toma menos de un minuto.'
              : 'Tap one. It takes less than a minute.'
            : t('auth.chooseRoleSub')}
        </p>
        <div className={`mx-auto mt-10 grid gap-5 ${buy ? 'max-w-2xl sm:grid-cols-2' : 'md:grid-cols-3'}`}>
          {shown.map((r) => (
            <button
              key={r.key}
              onClick={() => pickRole(r.key)}
              className="rounded-2xl border border-slate-200 bg-white p-6 text-left transition-all hover:-translate-y-1 hover:border-orange-300 hover:shadow-lg"
            >
              <r.icon className="h-8 w-8 text-orange-500" />
              <h2 className="mt-4 text-lg font-bold text-slate-900">{r.title}</h2>
              <p className="mt-2 text-sm text-slate-600">{r.desc}</p>
            </button>
          ))}
        </div>
        <MarketplaceDisclaimer variant="short" className="mt-8" />
        <p className="mt-8 text-center text-sm text-slate-500">
          {t('auth.haveAccount')}{' '}
          <Link to={authLink(path('/login'), nextUrl, buy || undefined)} className="font-bold text-orange-600">
            {t('nav.login')}
          </Link>
        </p>
      </div>
    );
  }

  const heading =
    role === 'homeowner'
      ? commercial
        ? t('auth.registerCommercial')
        : t('auth.registerHomeowner')
      : role === 'gc'
      ? t('auth.registerGc')
      : t('auth.registerSub');
  const tradeOptions = role === 'gc' ? gcTrades() : role === 'homeowner' ? homeownerCategories() : TRADES;

  return (
    <div className="mx-auto max-w-2xl px-4 py-8 sm:px-6 sm:py-10">
      <Seo title={heading} description={t('auth.chooseRoleSub')} noindex />
      {buy && <PurchaseSteps buy={buy} lang={lang} />}
      {/* ALREADY PAID -> say so first, so a buyer who just paid does not think
          this form is a second checkout. */}
      {afterPayment && (
        <div className="mb-4 flex items-start gap-3 rounded-xl border-2 border-emerald-300 bg-emerald-50 px-4 py-3">
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
          <p className="text-sm font-semibold leading-relaxed text-emerald-900">
            {lang === 'es'
              ? 'Pago recibido. Cree su cuenta con el MISMO correo con el que pagó y sus créditos se aplican solos. No se le cobrará de nuevo.'
              : 'Payment received. Create your account with the SAME email you paid with and your credits are applied automatically. You will not be charged again.'}
          </p>
        </div>
      )}
      {/* RETURNING CUSTOMERS clicking Buy land here too. Offer sign-in at the
          TOP — not below a long form they do not need to fill in. */}
      {(buy || afterPayment) && (
        <p className="mb-4 rounded-xl border border-slate-200 bg-white px-4 py-3 text-center text-sm text-slate-600">
          {lang === 'es' ? '¿Ya tiene cuenta? ' : 'Already have an account? '}
          <Link
            to={authLink(path('/login'), nextUrl, buy || undefined, f.email ? `email=${encodeURIComponent(f.email)}` : '')}
            className="font-bold text-orange-600 underline"
          >
            {afterPayment
              ? lang === 'es'
                ? 'Inicie sesión y reciba sus créditos'
                : 'Sign in and get your credits'
              : lang === 'es'
              ? 'Inicie sesión y continúe al pago'
              : 'Sign in and go to payment'}
          </Link>
        </p>
      )}
      {lean && role !== 'homeowner' ? (
        /* One-tap account type switch instead of a separate screen. */
        <div className="mb-5">
          <p className={labelCls}>{lang === 'es' ? 'Soy' : 'I am a'}</p>
          <div role="radiogroup" className="grid grid-cols-2 gap-2 rounded-xl bg-slate-100 p-1">
            {(['subcontractor', 'gc'] as UserType[]).map((r) => (
              <button
                key={r}
                type="button"
                role="radio"
                aria-checked={role === r}
                onClick={() => pickRole(r)}
                className={`inline-flex min-h-[48px] items-center justify-center gap-2 rounded-lg px-3 text-sm font-bold transition-colors ${
                  role === r ? 'bg-white text-slate-900 shadow-sm ring-1 ring-slate-200' : 'text-slate-500'
                }`}
              >
                {r === 'gc' ? <Building2 className="h-4 w-4" /> : <HardHat className="h-4 w-4" />}
                {r === 'gc' ? t('home.gcTitle') : t('home.subTitle')}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <button onClick={() => setRole(null)} className="mb-5 inline-flex min-h-[44px] items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-900">
          <ArrowLeft className="h-4 w-4" /> {t('common.back')}
        </button>
      )}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-8">
        <h1 className="text-2xl font-extrabold text-slate-900">{heading}</h1>
        {/* Commercial intent: explain that the SAME free project-owner account
            covers commercial work, so nobody hunts for an account type that
            does not exist. */}
        {role === 'homeowner' && commercial && (
          <p className="mt-2 rounded-lg bg-blue-50 px-3 py-2 text-sm text-slate-700">{t('auth.commercialNote')}</p>
        )}
        {role !== 'homeowner' && !lean && (
          <p className="mt-2 text-sm text-slate-500">
            {lang === 'es'
              ? 'No se requiere número de licencia para registrarse. Puede agregarlo después en Configuración de Perfil si lo desea.'
              : 'No license number is required to register. You can add one later in Profile Settings if you want to.'}
          </p>
        )}

        <form onSubmit={submit} noValidate className="mt-6 grid gap-4 sm:grid-cols-2">
          {role === 'homeowner' ? (
            <>
              <div><label className={labelCls}>{t('auth.firstName')}</label><input autoComplete="given-name" className={inputCls} value={f.first_name || ''} onChange={(e) => set('first_name', e.target.value)} /></div>
              <div><label className={labelCls}>{t('auth.lastName')}</label><input autoComplete="family-name" className={inputCls} value={f.last_name || ''} onChange={(e) => set('last_name', e.target.value)} /></div>
            </>
          ) : role === 'subcontractor' ? (
            /* Name FIRST, company OPTIONAL: many subs work under their own
               name and stalled on a "Company name" box they could not fill. */
            <>
              <div className="sm:col-span-2"><label className={labelCls}>{t('auth.ownerName')}</label><input autoComplete="name" className={inputCls} value={f.contact_name || ''} onChange={(e) => set('contact_name', e.target.value)} /></div>
              <div className="sm:col-span-2"><label className={labelCls}>{t('auth.companyName')} <span className="normal-case text-slate-400">({lang === 'es' ? 'opcional' : 'optional'})</span></label><input autoComplete="organization" className={inputCls} value={f.company_name || ''} onChange={(e) => set('company_name', e.target.value)} /></div>
            </>
          ) : (
            <>
              <div className="sm:col-span-2"><label className={labelCls}>{t('auth.companyName')}</label><input autoComplete="organization" className={inputCls} value={f.company_name || ''} onChange={(e) => set('company_name', e.target.value)} /></div>
              <div className="sm:col-span-2"><label className={labelCls}>{t('auth.contactName')}</label><input autoComplete="name" className={inputCls} value={f.contact_name || ''} onChange={(e) => set('contact_name', e.target.value)} /></div>
            </>
          )}

          <div><label className={labelCls}>{t('common.email')}</label><input type="email" inputMode="email" autoComplete="email" autoCapitalize="none" autoCorrect="off" spellCheck={false} className={inputCls} value={f.email || ''} onChange={(e) => set('email', e.target.value.trim())} /></div>
          <div><label className={labelCls}>{t('common.phone')}</label><input type="tel" inputMode="tel" autoComplete="tel" className={inputCls} value={f.phone || ''} onChange={(e) => set('phone', e.target.value)} /></div>
          <div><label className={labelCls}>{t('common.city')}</label><input autoComplete="address-level2" className={inputCls} value={f.city || ''} onChange={(e) => set('city', e.target.value)} /></div>
          <div>
            <label className={labelCls}>{t('common.state')}</label>
            <select autoComplete="address-level1" className={inputCls} value={f.state || ''} onChange={(e) => set('state', e.target.value)}>
              <option value="">—</option>
              {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          {/* FEWER BOXES DURING A PURCHASE. Years in business, employees and
              service areas are all optional and editable later in Profile
              Settings, so a buyer in the middle of Buy / Join never sees them. */}
          {role === 'subcontractor' && !lean && (
            <>
              <div><label className={labelCls}>{t('auth.years')} <span className="normal-case text-slate-400">({lang === 'es' ? 'opcional' : 'optional'})</span></label><input type="number" inputMode="numeric" min="0" className={inputCls} value={f.years_in_business || ''} onChange={(e) => set('years_in_business', e.target.value)} /></div>
              <div><label className={labelCls}>{t('auth.employees')} <span className="normal-case text-slate-400">({lang === 'es' ? 'opcional' : 'optional'})</span></label><input type="number" inputMode="numeric" min="0" className={inputCls} value={f.employees || ''} onChange={(e) => set('employees', e.target.value)} /></div>
            </>
          )}

          {role !== 'homeowner' && (
            <>
              {!lean && (
                <div className="sm:col-span-2">
                  <label className={labelCls}>{t('auth.serviceAreas')}</label>
                  <input className={inputCls} value={f.service_areas || ''} onChange={(e) => set('service_areas', e.target.value)} placeholder={f.city || 'Fontana, Riverside, Ontario'} />
                  <p className="mt-1 text-xs text-slate-400">
                    {lang === 'es' ? 'Opcional. Si lo deja vacío usamos su ciudad.' : 'Optional. Leave it empty and we use your city.'}
                  </p>
                </div>
              )}
              {/* TRADES. Required on a normal sign-up, OPTIONAL and folded away
                  during a purchase (or right after paying): a 50-chip list is a
                  long scroll on a phone and the dashboard already falls back to
                  the newest projects when no trade is set. */}
              {lean ? (
                <details className="sm:col-span-2 rounded-xl border border-slate-200 bg-slate-50">
                  <summary className="flex min-h-[48px] cursor-pointer list-none items-center justify-between gap-2 px-4 text-sm font-bold text-slate-700">
                    <span>
                      {role === 'gc' ? t('auth.tradesNeeded') : t('auth.tradeCategories')}{' '}
                      <span className="font-semibold normal-case text-slate-400">({lang === 'es' ? 'opcional' : 'optional'})</span>
                    </span>
                    <span className="truncate text-xs font-semibold text-slate-500">
                      {trades.length
                        ? trades.slice(0, 2).map((s) => tradeLabel(s, lang)).join(', ') + (trades.length > 2 ? ` +${trades.length - 2}` : '')
                        : lang === 'es' ? 'Elegir' : 'Choose'}
                    </span>
                  </summary>
                  <div className="flex flex-wrap gap-2 border-t border-slate-200 p-4">
                    {tradeOptions.map((tr) => (
                      <button
                        type="button"
                        key={tr.slug}
                        onClick={() => toggleTrade(tr.slug)}
                        className={`min-h-[36px] rounded-full border px-3 py-1.5 text-xs font-bold transition-colors ${
                          trades.includes(tr.slug) ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-300 bg-white text-slate-600'
                        }`}
                      >
                        {tradeLabel(tr.slug, lang)}
                      </button>
                    ))}
                  </div>
                </details>
              ) : (
                <div className="sm:col-span-2">
                  <label className={labelCls}>{role === 'gc' ? t('auth.tradesNeeded') : t('auth.tradeCategories')}</label>
                  <p className="mb-2 text-xs text-slate-400">{t('auth.selectTrades')}</p>
                  <div className="flex flex-wrap gap-2">
                    {tradeOptions.map((tr) => (
                      <button
                        type="button"
                        key={tr.slug}
                        onClick={() => toggleTrade(tr.slug)}
                        className={`min-h-[36px] rounded-full border px-3 py-1.5 text-xs font-bold transition-colors ${
                          trades.includes(tr.slug) ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-300 bg-white text-slate-600'
                        }`}
                      >
                        {tradeLabel(tr.slug, lang)}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          <div className="sm:col-span-2">
            <label htmlFor="reg-password" className={labelCls}>{t('auth.password')}</label>
            {/* Show/hide toggle: on a phone keyboard a mistyped hidden password
                is the #1 reason people cannot sign in again later. */}
            <div className="relative">
              <input id="reg-password" type={showPw ? 'text' : 'password'} autoComplete="new-password" className={`${inputCls} pr-20`} value={f.password || ''} onChange={(e) => set('password', e.target.value)} />
              <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute inset-y-0 right-0 px-3 text-xs font-bold text-slate-600 hover:text-slate-900">
                {showPw ? (lang === 'es' ? 'Ocultar' : 'Hide') : (lang === 'es' ? 'Mostrar' : 'Show')}
              </button>
            </div>
            <p className="mt-1 text-xs text-slate-400">{lang === 'es' ? 'Mínimo 6 caracteres.' : 'At least 6 characters.'}</p>
          </div>

          <div className="sm:col-span-2">
            <MarketplaceDisclaimer variant="full" role={role} />
          </div>

          <div className="sm:col-span-2">
            <AcceptTermsCheckbox checked={accepted} onChange={setAccepted} kind="register" />
          </div>

          {/* A2P 10DLC — SEPARATE from the required Terms box above, OPTIONAL,
              UNCHECKED by default. Leaving it empty never blocks sign-up. */}
          <div className="sm:col-span-2">
            <SmsConsentCheckbox checked={smsConsent} onChange={setSmsConsent} variant="boxed" id="register-sms-consent" />
          </div>

          <div className="sm:col-span-2">
            <Turnstile
              action="register"
              onVerify={setCaptcha}
              onStatus={setCaptchaStatus}
              resetKey={captchaReset}
              label={t('auth.captchaLabel')}
            />
          </div>

          {error && (
            <div role="alert" id="gg-register-error" className="sm:col-span-2 rounded-lg bg-red-50 px-3 py-2 text-sm font-semibold text-red-700">
              <p>{error}</p>
              {/* EMAIL ALREADY HAS AN ACCOUNT -> one tap to sign in with it
                  pre-filled, keeping the lead / plan they were buying. */}
              {existing && (
                <Link
                  to={authLink(path('/login'), nextUrl, buy || undefined, `email=${encodeURIComponent(f.email || '')}`)}
                  className="mt-2 flex min-h-[44px] items-center justify-center rounded-lg bg-slate-900 px-4 text-sm font-bold text-white hover:bg-slate-800"
                >
                  {lang === 'es' ? 'Iniciar sesión con este correo' : 'Sign in with this email'}
                </Link>
              )}
            </div>
          )}

          <div className="sm:col-span-2">
            {/* Never greyed out just because the terms box is unticked — a dead
                grey button gave no reason. Tapping it now SAYS what is missing. */}
            <button
              type="submit"
              disabled={busy}
              className="w-full rounded-xl bg-orange-500 px-4 py-3.5 text-base font-bold text-white hover:bg-orange-600 disabled:opacity-50"
            >
              {busy
                ? t('auth.creating')
                : afterPayment
                ? lang === 'es'
                  ? 'Crear cuenta y recibir mis créditos'
                  : 'Create account & get my credits'
                : buy
                ? lang === 'es'
                  ? 'Crear cuenta y continuar al pago'
                  : 'Create account & continue to payment'
                : t('auth.createAccount')}
            </button>
            <p className="mt-3 text-center text-sm text-slate-500">
              {t('auth.haveAccount')}{' '}
              <Link to={authLink(path('/login'), nextUrl, buy || undefined)} className="font-bold text-orange-600">
                {t('nav.login')}
              </Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Register;
